import { baseApi } from './baseApi';
import type { CreateTerminalIntentRequest, TerminalPaymentIntent } from './terminalPaymentTypes';

// Tipos compartidos entre proveedores de terminal — viven en terminalPaymentTypes
export type { CreateTerminalIntentRequest, TerminalIntentStatus, TerminalPaymentIntent } from './terminalPaymentTypes';

export interface MercadoPagoConfig {
  enabled: boolean;
  isConfigured: boolean;
  hasAccessToken: boolean;
  hasWebhookSecret: boolean;
  accessTokenHint?: string;
  webhookUrl: string;
  defaultTerminalId?: string;
}

export interface SaveMercadoPagoConfigRequest {
  accessToken?: string;
  webhookSecret?: string;
  enabled?: boolean;
  defaultTerminalId?: string;
}

export interface MpTerminal {
  id: string;
  posId?: string;
  storeId?: string;
  externalPosId?: string;
  operatingMode: string; // 'PDV' | 'STANDALONE'
}

export const mercadoPagoApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMercadoPagoConfig: builder.query<MercadoPagoConfig, void>({
      query: () => '/mercadopago/config',
      providesTags: ['MercadoPagoConfig'],
    }),

    saveMercadoPagoConfig: builder.mutation<MercadoPagoConfig, SaveMercadoPagoConfigRequest>({
      query: (body) => ({
        url: '/mercadopago/config',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['MercadoPagoConfig', 'AppSettings'],
    }),

    getMpTerminals: builder.query<MpTerminal[], void>({
      query: () => '/mercadopago/terminals',
      providesTags: ['MercadoPagoConfig'],
    }),

    setMpTerminalOperatingMode: builder.mutation<MpTerminal, { terminalId: string; operatingMode: string }>({
      query: ({ terminalId, operatingMode }) => ({
        url: `/mercadopago/terminals/${encodeURIComponent(terminalId)}/operating-mode`,
        method: 'POST',
        body: { operatingMode },
      }),
      invalidatesTags: ['MercadoPagoConfig'],
    }),

    createTerminalIntent: builder.mutation<TerminalPaymentIntent, CreateTerminalIntentRequest>({
      query: (body) => ({
        url: '/mercadopago/intents',
        method: 'POST',
        body,
      }),
    }),

    // Estado del cobro en curso. El modal escucha TerminalPaymentChanged por
    // SignalR y además hace polling lento como fallback (pollingInterval en el
    // consumidor) — misma regla que el resto del tiempo real.
    getTerminalIntent: builder.query<TerminalPaymentIntent, string>({
      query: (intentId) => `/mercadopago/intents/${intentId}`,
    }),

    // Intent Pending vigente de una cuenta (o null): reanudar cobro abandonado.
    getMpPendingIntent: builder.query<TerminalPaymentIntent | null, { orderId?: string; splitId?: string }>({
      query: ({ orderId, splitId }) => {
        const params = new URLSearchParams();
        if (orderId) params.set('orderId', orderId);
        if (splitId) params.set('splitId', splitId);
        return `/mercadopago/intents/pending?${params.toString()}`;
      },
    }),

    cancelTerminalIntent: builder.mutation<TerminalPaymentIntent, string>({
      query: (intentId) => ({
        url: `/mercadopago/intents/${intentId}/cancel`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetMercadoPagoConfigQuery,
  useSaveMercadoPagoConfigMutation,
  useGetMpTerminalsQuery,
  useLazyGetMpTerminalsQuery,
  useSetMpTerminalOperatingModeMutation,
  useCreateTerminalIntentMutation,
  useGetTerminalIntentQuery,
  useLazyGetTerminalIntentQuery,
  useGetMpPendingIntentQuery,
  useCancelTerminalIntentMutation,
} = mercadoPagoApi;
